import React, { FC, MouseEvent, useState } from 'react';
import {
  FunctionField,
  FunctionFieldProps,
  useDataProvider,
  useNotify,
  useRefresh,
} from 'react-admin';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import CheckCircle from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import { makeStyles } from '@material-ui/core/styles';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { Point } from 'geojson';
import { JobInterface, JobSheetInterface } from '@vatos-pas/common';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { CAN_CHANGE_JOB } from 'providers/permissions';

type JobAddressRecord = Pick<
  JobInterface,
  'id' | 'address1' | 'address2' | 'city' | 'state' | 'zip'
>;

const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';
const FAILED_INTEGRATION_MESSAGE =
  'Address Integration Failed — Please update the address manually in the Job form';
const SUCCESS_MESSAGE = 'Address Found and Integrated Successfully';
const TIMEOUT_MESSAGE = 'Address lookup timed out. Please try again.';
const GEOCODE_TIMEOUT_MS = 30_000;
const GEOCODE_TIMEOUT_CODE = 'GEOCODE_TIMEOUT';

const withTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const id = setTimeout(
      () => reject(new Error(GEOCODE_TIMEOUT_CODE)),
      ms,
    );
    promise.then(
      value => {
        clearTimeout(id);
        resolve(value);
      },
      error => {
        clearTimeout(id);
        reject(error);
      },
    );
  });

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  integrated: {
    color: theme.palette.success.main,
  },
  notIntegrated: {
    color: theme.palette.text.primary,
  },
  button: {
    padding: 0,
  },
}));

const buildAddressQuery = (job: JobAddressRecord): string => {
  const { address1, address2, city, state, zip } = job;
  const street = address2 ? `${address1} ${address2}` : address1;
  return `${street} , ${city} , ${state}  ${zip}`;
};

const getGeocoderStatus = (error: unknown): string | null => {
  if (typeof error !== 'object' || error === null || !('code' in error)) {
    return null;
  }
  const { code } = error as { code: unknown };
  return typeof code === 'string' ? code : null;
};

type ClickableErrorIconProps = {
  record: JobSheetInterface;
};

const ClickableErrorIcon: FC<ClickableErrorIconProps> = ({ record }) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const geocodingLib = useMapsLibrary('geocoding');
  const [loading, setLoading] = useState(false);

  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (!geocodingLib) {
      notify(GENERIC_ERROR_MESSAGE, 'error');
      return;
    }

    setLoading(true);

    try {
      let job: JobAddressRecord;
      try {
        const response = await dataProvider.getOne<JobAddressRecord>('job', {
          id: record.id,
        });
        job = response.data;
      } catch {
        notify(GENERIC_ERROR_MESSAGE, 'error');
        return;
      }

      const address = buildAddressQuery(job);

      let response: google.maps.GeocoderResponse;
      try {
        const geocoder = new geocodingLib.Geocoder();
        response = await withTimeout(
          geocoder.geocode({
            address,
            componentRestrictions: { country: 'us' },
          }),
          GEOCODE_TIMEOUT_MS,
        );
      } catch (error) {
        if (error instanceof Error && error.message === GEOCODE_TIMEOUT_CODE) {
          notify(TIMEOUT_MESSAGE, 'error');
        } else if (getGeocoderStatus(error) === 'ZERO_RESULTS') {
          notify(FAILED_INTEGRATION_MESSAGE, 'error');
        } else {
          notify(GENERIC_ERROR_MESSAGE, 'error');
        }
        return;
      }

      const results = response.results;

      if (results.length === 0) {
        notify(FAILED_INTEGRATION_MESSAGE, 'error');
        return;
      }

      if (results.length > 1) {
        notify(FAILED_INTEGRATION_MESSAGE, 'error');
        return;
      }

      const location = results[0].geometry.location;
      const lat = location.lat();
      const lng = location.lng();

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        notify(FAILED_INTEGRATION_MESSAGE, 'error');
        return;
      }

      const geo: Point = { type: 'Point', coordinates: [lng, lat] };

      try {
        await dataProvider.update('job', {
          id: record.id,
          data: { geo },
          previousData: record,
        });
      } catch {
        notify(GENERIC_ERROR_MESSAGE, 'error');
        return;
      }

      notify(SUCCESS_MESSAGE);
    } finally {
      setLoading(false);
      refresh();
    }
  };

  return (
    <Tooltip title="Click to integrate address">
      <IconButton
        className={classes.button}
        onClick={handleClick}
        disabled={loading}
        data-testid="address-integration-click-button"
        aria-label="Click to integrate address"
      >
        {loading ? (
          <CircularProgress
            size={20}
            data-testid="address-integration-loading"
          />
        ) : (
          <ErrorIcon
            className={classes.notIntegrated}
            data-testid="address-integration-icon-not-integrated"
          />
        )}
      </IconButton>
    </Tooltip>
  );
};

const AddressIntegrationField: FC<
  Omit<FunctionFieldProps<JobSheetInterface>, 'render'>
> = props => {
  const classes = useStyles();
  const { hasPermission } = PermissionsProvider.useContainer();
  const canChangeJob = hasPermission(CAN_CHANGE_JOB);

  return (
    <FunctionField<JobSheetInterface>
      {...props}
      render={record => {
        if (!record) return null;
        const isIntegrated = Boolean(record.geo);

        return (
          <Box
            className={classes.wrapper}
            data-testid="address-integration-field"
          >
            {isIntegrated ? (
              <CheckCircle
                className={classes.integrated}
                data-testid="address-integration-icon-integrated"
              />
            ) : canChangeJob ? (
              <ClickableErrorIcon record={record} />
            ) : (
              <ErrorIcon
                className={classes.notIntegrated}
                data-testid="address-integration-icon-not-integrated"
              />
            )}
          </Box>
        );
      }}
    />
  );
};

export default AddressIntegrationField;
